/**
 * PF v2 file hub — Wave-2 kind (2c): floor-plan company logo.
 * ============================================================================
 *
 * The floor-plan configurator's company-logo upload, moved onto the file hub —
 * closing the LAST non-FileAPI upload in PF v2 (legacy rode
 * `usePurchaseSessionVisualStore`/`usePurchaseSessionVisualOperations`, Zustand +
 * manual axios). It mints against the same v2 session-file resource as staging
 * (`kind: VISUAL`), so this kind is a near-clone of
 * `STAGING_VISUALS_KIND`: it reuses that transport, `resolveVisualDerivative`, and
 * the `PfV2VisualMetadata` shape verbatim.
 *
 * N1 (multi-derivative displayUrl clobber) — DISSOLVED for the shipped kind:
 * legacy triggered RAW_THUMB + RAW_WEB, but the logo zone only DISPLAYS
 * RAW_THUMB. This kind declares a SINGLE display derivative `[RAW_THUMB]` → with
 * one derivative the clobber cannot occur. The legacy RAW_WEB pre-warm poll is
 * dropped (FE never rendered it; BE derivative generation is independent of the
 * FE download poll) — DF-raw-web-prewarm. The `displayDerivative` hub hardening
 * (Wave-2 foundation) additionally closes N1 as a class for any future
 * N-derivative kind.
 *
 * A-04: STORAGE-AGNOSTIC — transport + projection only. The floorPlanConfig
 * MODULE owns the projection→DTO sync (interaction-gated + value-guarded, §4b —
 * `useFloorPlanLogoSync`).
 */

import { FileAction, FileState, type FileAPIEntry } from 'components/common/FileAPI'
import { VisualFileType } from 'constants/visual'

import { SessionFileKind } from 'models/purchaseFlowRevamp'

import { createFileHubKind } from '../createFileHubKind'
import { makeSessionFileDeleteHandler, makeSessionFileUploadResolver } from '../sessionFiles.transport'
import type { FileHubKind } from '../fileHub.types'
import type { PfV2VisualMetadata } from './stagingVisuals'

// ─── Scope ────────────────────────────────────────────────────────────────────

/**
 * Static, session-agnostic FileAPI scope for the floor-plan logo. Static (not
 * keyed per-session) is correct: PF v2 has exactly one draft/session alive, the
 * provider owns this scope once at the PF-v2 root, and the session id is read
 * live inside the resolvers. Keeping it static also avoids the destructive
 * `INIT_SCOPE` re-firing if the scope string changed mid-flight.
 */
export const PF_V2_FLOOR_PLAN_LOGO_SCOPE = 'PF_V2_FLOOR_PLAN_LOGO'

// ─── Upload params ─────────────────────────────────────────────────────────────

/** The cart item's catalogue `productCode`, sent to the mint verbatim. `null`
 *  rejects cleanly (chip → `FileState.ERROR`) rather than minting against a blank
 *  code for the backend to refuse. */
export interface FloorPlanLogoUploadParams {
  productCode: string | null
}

// ─── Projection ────────────────────────────────────────────────────────────────

export interface FloorPlanLogoProjection {
  /**
   * The ORIGINAL filename (wire-compat with `FloorPlanConfigDraftDTO.companyLogo`,
   * which legacy set to `file.name` — NOT the gcFilename). `null` when no logo.
   */
  companyLogoFilename: string | null
  /** RAW_THUMB signed URL for the zone preview (`null` until the derivative resolves). */
  displayUrl: string | null
  /** An upload is transferring OR the thumb derivative is still polling. */
  isUploading: boolean
}

/**
 * Derived read model off `allFilesArray`. Single logo, latest-wins (legacy
 * replaced the existing one). `companyLogoFilename` reads `originalFilename` so
 * it byte-matches the DTO marker legacy persisted (`setCompanyLogo(file.name)`).
 */
export function projectFloorPlanLogo(
  entries: Array<FileAPIEntry<PfV2VisualMetadata>>
): FloorPlanLogoProjection {
  const relevant = entries.filter((e) => e.action !== FileAction.DELETE)
  const isUploading =
    relevant.some((e) => e.state === FileState.RUNNING) ||
    relevant.some((e) => e.state === FileState.SUCCESS && !e.metadata?.derivativeResolved)

  const ready = relevant.filter(
    (e) => e.state === FileState.SUCCESS && (e.action === FileAction.UPLOAD || e.action === FileAction.RE_UPLOAD)
  )
  // Single logo; latest wins (legacy replaces the existing one).
  const first = ready[ready.length - 1]

  return {
    companyLogoFilename: first?.originalFilename ?? null,
    displayUrl: first?.displayUrl ?? null,
    isUploading,
  }
}

// ─── Kind descriptor ─────────────────────────────────────────────────────────

export const FLOOR_PLAN_LOGO_KIND: FileHubKind<
  PfV2VisualMetadata,
  FloorPlanLogoProjection,
  FloorPlanLogoUploadParams
> = {
  scope: PF_V2_FLOOR_PLAN_LOGO_SCOPE,
  // Single DISPLAY derivative → N1 dissolved for the shipped kind (RAW_WEB
  // pre-warm dropped; DF-raw-web-prewarm).
  derivatives: [VisualFileType.RAW_THUMB],
  purgeOnDestroy: true,
  makeUploadResolver: (ctx, params) =>
    makeSessionFileUploadResolver<PfV2VisualMetadata>(ctx, {
      productCode: params.productCode,
      kind: SessionFileKind.VISUAL,
    }),
  makeDeleteHandler: (ctx) => makeSessionFileDeleteHandler<PfV2VisualMetadata>(ctx),
  resolveTag: (params) => String(params.productCode),
  project: projectFloorPlanLogo,
}

// ─── Provider + hook ────────────────────────────────────────────────────────────

const [FloorPlanLogoProvider, useFloorPlanLogoUpload] = createFileHubKind(FLOOR_PLAN_LOGO_KIND)

/**
 * The single owner of the PF-v2 floor-plan-logo FileAPI scope. Mounted ONCE at
 * the PF-v2 root (`PurchaseFlowRevamp.page`, wrapping `<Routes>`) next to the
 * Visual / PoA / AdditionalInfo providers so the scope survives step/tab
 * switches; the default `purgeOnDestroy` clears it on leaving
 * `/purchase-flow-revamp/*`. The logo has ONE consumer (the config tab) but the
 * destructive `INIT_SCOPE` rule still mandates a single hoisted provider.
 */
export const PfV2FloorPlanLogoProvider = FloorPlanLogoProvider

export { useFloorPlanLogoUpload }
